import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type YourMiamiOrthodontistLocalization = {
	heroTitle: string;
	heading: string;
	doctorHeading: string;
	doctorParagraphs: Array<string>;
	differenceHeading: string;
	differenceParagraph: string;
	mattersHeading: string;
	mattersParagraph: string;
	listPrefixBeforeOrtho: string;
	listOrthoLink: string;
	listPrefixBeforeBraces: string;
	listBracesLink: string;
	listSuffix: string;
	bullets: Array<string>;
	treatmentBulletPrefix: string;
	treatmentBulletLink: string;
	treatmentBulletSuffix: string;
	pricePrefix: string;
	priceLink: string;
	priceSuffix: string;
};

const yourMiamiOrthodontistLocalization = {
	[Locale.EN]: {
		heroTitle: "Your Miami Orthodontist",
		heading: "Meet Your Miami Orthodontist",
		doctorHeading: "Meet Dr. Garcia-Paul",
		doctorParagraphs: [
			"Dr. Carmen Garcia-Paul was born and raised in Miami and—despite leaving the area for graduate studies—has always considered the area her home.",
			"She graduated from Our Lady of Lourdes Academy where she was both a scholar-athlete and valedictorian of her class. After Lourdes, Dr. Garcia-Paul was accepted into the honors program at the University of Miami.",
			"While an undergraduate, Dr. Garcia-Paul began working for several local dentists and dental specialists in Miami. It was during this time that she first became interested in the specialty of orthodontics.",
			"After seeing the positive effect that orthodontics could have on a person’s self-esteem, personality, and confidence, she decided to dedicate her life to helping others by perfecting their smiles.",
		],
		differenceHeading: "How orthodontists and dentists differ",
		differenceParagraph:
			"While orthodontists and dentists have similar backgrounds, the two professions are actually quite different. In fact, all orthodontist are trained as dentists first, but in addition to completing dental school to become a dentist, orthodontists continue their education for a few additional years to earn the license and certification necessary to be considered an orthodontist. That means that orthodontists have spent years studying nothing but how and why to move teeth. That education includes what to do, how to plan and how to execute the appropriate treatment plan for patients' unique cases.",
		mattersHeading: "Why seeing an orthodontist matters",
		mattersParagraph:
			"If you're hoping to achieve a beautiful, straight forever smile that you can feel confident in, it's important that you see an orthodontist to help guide you on your journey. This is because the additional training that orthodontists undergo helps them develop the skills necessary to diagnose and treat more complex orthodontic cases. Dentists also receive basic orthodontic training which enables them to perform root canals, extract wisdom teeth and treat young children who do not need to see an orthodontist yet.",
		listPrefixBeforeOrtho: "Seeing your ",
		listOrthoLink: "Miami orthodontist",
		listPrefixBeforeBraces: " may change your experience in ",
		listBracesLink: "braces",
		listSuffix: " by:",
		bullets: [
			"Providing additional treatment options.",
			"Knowing that you'll receive the best possible outcome on your treatment plan, especially if you have severe bite problems that need to be addressed.",
			"Personal experience treating multiple cases that share your unique concerns",
			"Being seen more quickly, as a family dentist will be focused on longer patient cases or emergencies throughout the day.",
			"You may think it's easier to have your orthodontic work completed by your dentist. Convenience shouldn't be a concern, because you will only be stopping by your Miami orthodontist's offices every six to eight weeks.",
		],
		treatmentBulletPrefix: "Giving you more efficient ",
		treatmentBulletLink: "treatment",
		treatmentBulletSuffix:
			" and visits, because it is the office's primary area of care.",
		pricePrefix: "And don't let price be a problem either! ",
		priceLink: "Miami Orthodontic Specialists",
		priceSuffix: " is proud to provide affordable treatment in the Miami area.",
	},
	[Locale.ES]: {
		heroTitle: "Tu ortodoncista en Miami",
		heading: "Conoce a tu ortodoncista en Miami",
		doctorHeading: "Conoce a la Dra. Garcia-Paul",
		doctorParagraphs: [
			"La Dra. Carmen Garcia-Paul nació y creció en Miami y, aunque salió del área para realizar estudios de posgrado, siempre ha considerado esta zona su hogar.",
			"Se graduó de Our Lady of Lourdes Academy, donde fue atleta académica y valedictorian de su clase. Después de Lourdes, la Dra. Garcia-Paul fue aceptada en el programa de honores de la University of Miami.",
			"Mientras era estudiante universitaria, la Dra. Garcia-Paul comenzó a trabajar con varios dentistas y especialistas dentales locales en Miami. Fue durante este tiempo cuando se interesó por primera vez en la especialidad de ortodoncia.",
			"Después de ver el efecto positivo que la ortodoncia podía tener en la autoestima, personalidad y confianza de una persona, decidió dedicar su vida a ayudar a los demás perfeccionando sus sonrisas.",
		],
		differenceHeading: "Cómo se diferencian los ortodoncistas y los dentistas",
		differenceParagraph:
			"Aunque los ortodoncistas y los dentistas tienen antecedentes similares, las dos profesiones son bastante diferentes. De hecho, todos los ortodoncistas se forman primero como dentistas, pero además de completar la escuela dental para convertirse en dentistas, continúan su educación durante algunos años más para obtener la licencia y certificación necesarias para ser considerados ortodoncistas. Eso significa que los ortodoncistas han pasado años estudiando exclusivamente cómo y por qué mover los dientes. Esa educación incluye qué hacer, cómo planificar y cómo ejecutar el plan de tratamiento adecuado para el caso único de cada paciente.",
		mattersHeading: "Por qué es importante ver a un ortodoncista",
		mattersParagraph:
			"Si esperas lograr una sonrisa hermosa, recta y duradera en la que puedas sentir confianza, es importante que consultes a un ortodoncista para que te guíe en tu camino. Esto se debe a que la capacitación adicional de los ortodoncistas les ayuda a desarrollar las habilidades necesarias para diagnosticar y tratar casos ortodóncicos más complejos. Los dentistas también reciben capacitación ortodóncica básica, lo que les permite realizar tratamientos de conducto, extraer muelas del juicio y tratar a niños pequeños que aún no necesitan ver a un ortodoncista.",
		listPrefixBeforeOrtho: "Ver a tu ",
		listOrthoLink: "ortodoncista en Miami",
		listPrefixBeforeBraces: " puede cambiar tu experiencia con los ",
		listBracesLink: "brackets",
		listSuffix: " al:",
		bullets: [
			"Ofrecer opciones de tratamiento adicionales.",
			"Saber que recibirás el mejor resultado posible en tu plan de tratamiento, especialmente si tienes problemas severos de mordida que deben corregirse.",
			"Contar con experiencia personal tratando múltiples casos que comparten tus inquietudes únicas.",
			"Ser atendido más rápidamente, ya que un dentista familiar estará concentrado en casos más largos o emergencias durante el día.",
			"Quizás pienses que es más fácil completar tu trabajo ortodóncico con tu dentista. La conveniencia no debería ser una preocupación, porque solo visitarás la oficina de tu ortodoncista en Miami cada seis a ocho semanas.",
		],
		treatmentBulletPrefix: "Darte un ",
		treatmentBulletLink: "tratamiento",
		treatmentBulletSuffix:
			" y visitas más eficientes, porque es el área principal de atención de la oficina.",
		pricePrefix: "Y no dejes que el precio sea un problema. ",
		priceLink: "Miami Orthodontic Specialists",
		priceSuffix:
			" se enorgullece de ofrecer tratamiento asequible en el área de Miami.",
	},
} satisfies Record<Locale, YourMiamiOrthodontistLocalization>;

export function YourMiamiOrthodontistPage() {
	const getLocalization = useLocalization(yourMiamiOrthodontistLocalization);
	const doctorParagraphs = getLocalization("doctorParagraphs");
	const bullets = getLocalization("bullets");

	return (
		<Flex direction="column">
		<HeroStyle2
				showFullImageOnMobile
				heroImageUrl="/about-dr.jpg"
				title={getLocalization("heroTitle")}
				backgroundPosition="top"
			/>
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<Heading as="h2" className={styles.postTitle}>
						{getLocalization("heading")}
					</Heading>
					<Grid
						columns={{ initial: "1", md: "2" }}
						gap="6"
						className={styles.orthodontistIntroGrid}
					>
						<OptimizedImage
							src="/Dr-Garcia-Paul.jpg"
							alt="Miami Family Orthodontist"
							width={900}
							height={900}
							className={styles.orthodontistDoctorPhoto}
						/>
						<Box className={styles.entryContent}>
							<Heading as="h3" className={styles.relatedTitle}>
								{getLocalization("doctorHeading")}
							</Heading>
							{doctorParagraphs.map((paragraph) => (
								<Text as="p" size="4" key={paragraph}>
									{paragraph}
								</Text>
							))}
						</Box>
					</Grid>
					<Box className={styles.entryContent}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("differenceHeading")}
						</Heading>
						<Text as="p" size="4">
							{getLocalization("differenceParagraph")}
						</Text>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("mattersHeading")}
						</Heading>
						<Text as="p" size="4">
							{getLocalization("mattersParagraph")}
						</Text>
						<Text as="p" size="4">
							{getLocalization("listPrefixBeforeOrtho")}
							<Link to="/miami-family-orthodontist">
								{getLocalization("listOrthoLink")}
							</Link>
							{getLocalization("listPrefixBeforeBraces")}
							<Link to="/miami-braces">
								{getLocalization("listBracesLink")}
							</Link>
							{getLocalization("listSuffix")}
						</Text>
						<ul>
							<li>{bullets[0]}</li>
							<li>
								{getLocalization("treatmentBulletPrefix")}
								<Link to="/our-treatments">
									{getLocalization("treatmentBulletLink")}
								</Link>
								{getLocalization("treatmentBulletSuffix")}
							</li>
							{bullets.slice(1).map((bullet) => (
								<li key={bullet}>{bullet}</li>
							))}
						</ul>
						<Text as="p" size="4">
							{getLocalization("pricePrefix")}
							<Link to="/">{getLocalization("priceLink")}</Link>
							{getLocalization("priceSuffix")}
						</Text>
					</Box>
				</Box>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
